import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  FileSignature, Plus, Download, Trash2, Copy, Loader2,
  CheckCircle, Clock, Link as LinkIcon, ExternalLink, Edit2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  civilite: 'Madame',
  prestataire_nom: '',
  prestataire_birth_date: '',
  prestataire_birth_place: '',
  prestataire_siret: '',
  prestataire_address: '',
  prestataire_email: '',
  contract_start_date: '',
  signature_place: 'Beaussais-Sur-Mer',
};

const StatusBadge = ({ status }) => {
  if (status === 'signed') {
    return (
      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
        <CheckCircle className="h-3 w-3 mr-1" /> Signé
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
      <Clock className="h-3 w-3 mr-1" /> En attente
    </Badge>
  );
};

export default function ContractsManagement() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [createdContract, setCreatedContract] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contracts`);
      setContracts(data || []);
    } catch (e) {
      toast.error('Impossible de charger les contrats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateForm = () => {
    const required = [
      ['prestataire_nom', 'Nom complet'],
      ['prestataire_birth_date', 'Date de naissance'],
      ['prestataire_birth_place', 'Lieu de naissance'],
      ['prestataire_siret', 'SIRET'],
      ['prestataire_address', 'Adresse postale'],
      ['prestataire_email', 'E-mail'],
      ['contract_start_date', 'Date d\u2019entr\u00e9e en vigueur'],
    ];
    for (const [k, label] of required) {
      if (!form[k] || !form[k].toString().trim()) {
        toast.error(`Champ requis : ${label}`);
        return false;
      }
    }
    const siretDigits = (form.prestataire_siret || '').replace(/\D+/g, '');
    if (siretDigits.length !== 14) {
      toast.error('SIRET invalide (14 chiffres requis)');
      return false;
    }
    const dateRe = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!dateRe.test(form.prestataire_birth_date)) {
      toast.error('Date de naissance au format JJ/MM/AAAA');
      return false;
    }
    if (!dateRe.test(form.contract_start_date)) {
      toast.error('Date d\u2019entr\u00e9e en vigueur au format JJ/MM/AAAA');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.prestataire_email)) {
      toast.error('E-mail invalide');
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/contracts/${editingId}`, form);
        toast.success('Contrat mis à jour.');
      } else {
        const { data } = await axios.post(`${API}/contracts`, form);
        setCreatedContract(data);
        toast.success('Contrat créé. Envoyez le lien de signature au prestataire.');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      await fetchContracts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingId ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création'));
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (contract) => {
    if (contract.status === 'signed') {
      toast.error('Impossible de modifier un contrat signé');
      return;
    }
    setForm({
      civilite: contract.civilite || 'Madame',
      prestataire_nom: contract.prestataire_nom || '',
      prestataire_birth_date: contract.prestataire_birth_date || '',
      prestataire_birth_place: contract.prestataire_birth_place || '',
      prestataire_siret: contract.prestataire_siret || '',
      prestataire_address: contract.prestataire_address || '',
      prestataire_email: contract.prestataire_email || '',
      contract_start_date: contract.contract_start_date || '',
      signature_place: contract.signature_place || 'Beaussais-Sur-Mer',
    });
    setEditingId(contract.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const copyLink = (url) => {
    const full = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(full).then(
      () => toast.success('Lien copié dans le presse-papiers'),
      () => toast.error('Impossible de copier'),
    );
  };

  const downloadPdf = async (contract) => {
    try {
      const response = await axios.get(`${API}/contracts/${contract.id}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (contract.prestataire_nom || 'contrat').replace(/[^A-Za-z0-9-_]+/g, '_');
      a.download = `Contrat_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur de téléchargement');
    }
  };

  const deleteContract = async (contract) => {
    if (contract.status === 'signed') {
      toast.error('Impossible de supprimer un contrat signé');
      return;
    }
    if (!window.confirm(`Supprimer le contrat de ${contract.prestataire_nom} ?`)) return;
    try {
      await axios.delete(`${API}/contracts/${contract.id}`);
      toast.success('Contrat supprimé');
      fetchContracts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 p-4 md:p-8 md:ml-64">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
                <FileSignature className="h-7 w-7 text-blue-600" />
                Contrats prestataires
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Générez et faites signer électroniquement les contrats d'entretien
              </p>
            </div>
            <Button
              onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="new-contract-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Nouveau contrat
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Liste des contrats</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : contracts.length === 0 ? (
                <p className="text-center text-slate-500 py-8 text-sm">
                  Aucun contrat pour le moment. Créez votre premier contrat avec le bouton ci-dessus.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Prestataire</TableHead>
                        <TableHead>SIRET</TableHead>
                        <TableHead>Entrée en vigueur</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Créé le</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contracts.map((c) => (
                        <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                          <TableCell>
                            <div className="font-medium text-slate-800">
                              {c.civilite} {c.prestataire_nom}
                            </div>
                            <div className="text-xs text-slate-500">{c.prestataire_email}</div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{c.prestataire_siret}</TableCell>
                          <TableCell className="text-sm">{c.contract_start_date}</TableCell>
                          <TableCell>
                            <StatusBadge status={c.status} />
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {c.status !== 'signed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEdit(c)}
                                  title="Modifier le brouillon"
                                  data-testid={`edit-contract-${c.id}`}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              )}
                              {c.status !== 'signed' && c.sign_url && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => copyLink(c.sign_url)}
                                  title="Copier le lien de signature"
                                  data-testid={`copy-link-${c.id}`}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              )}
                              {c.status !== 'signed' && c.sign_url && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(c.sign_url, '_blank')}
                                  title="Ouvrir le lien de signature"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadPdf(c)}
                                title="Télécharger le PDF"
                                data-testid={`download-pdf-${c.id}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {c.status !== 'signed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => deleteContract(c)}
                                  title="Supprimer le brouillon"
                                  data-testid={`delete-contract-${c.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Form modal */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifier le contrat' : 'Nouveau contrat prestataire'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div>
              <Label>Civilité *</Label>
              <Select value={form.civilite} onValueChange={(v) => update('civilite', v)}>
                <SelectTrigger data-testid="form-civilite">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Madame">Madame</SelectItem>
                  <SelectItem value="Monsieur">Monsieur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nom complet *</Label>
              <Input
                value={form.prestataire_nom}
                onChange={(e) => update('prestataire_nom', e.target.value)}
                placeholder="Anastasia LAINE"
                data-testid="form-nom"
              />
            </div>
            <div>
              <Label>Date de naissance * (JJ/MM/AAAA)</Label>
              <Input
                value={form.prestataire_birth_date}
                onChange={(e) => update('prestataire_birth_date', e.target.value)}
                placeholder="24/01/2003"
                data-testid="form-birth-date"
              />
            </div>
            <div>
              <Label>Lieu de naissance *</Label>
              <Input
                value={form.prestataire_birth_place}
                onChange={(e) => update('prestataire_birth_place', e.target.value)}
                placeholder="Dinan (22)"
                data-testid="form-birth-place"
              />
            </div>
            <div>
              <Label>SIRET * (14 chiffres)</Label>
              <Input
                value={form.prestataire_siret}
                onChange={(e) => update('prestataire_siret', e.target.value)}
                placeholder="93470253100013"
                data-testid="form-siret"
              />
            </div>
            <div>
              <Label>E-mail *</Label>
              <Input
                type="email"
                value={form.prestataire_email}
                onChange={(e) => update('prestataire_email', e.target.value)}
                placeholder="prestataire@example.com"
                autoCapitalize="none"
                autoCorrect="off"
                data-testid="form-email"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Adresse postale complète *</Label>
              <Input
                value={form.prestataire_address}
                onChange={(e) => update('prestataire_address', e.target.value)}
                placeholder="123 Rue de l'Exemple, 35960 LE VIVIER SUR MER"
                data-testid="form-address"
              />
            </div>
            <div>
              <Label>Entrée en vigueur * (JJ/MM/AAAA)</Label>
              <Input
                value={form.contract_start_date}
                onChange={(e) => update('contract_start_date', e.target.value)}
                placeholder="01/03/2026"
                data-testid="form-start-date"
              />
            </div>
            <div>
              <Label>Lieu de signature</Label>
              <Input
                value={form.signature_place}
                onChange={(e) => update('signature_place', e.target.value)}
                data-testid="form-signature-place"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeForm} disabled={submitting}>
              Annuler
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="form-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? 'Enregistrer les modifications' : 'Créer & générer le lien'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Created confirmation modal with signing link */}
      <Dialog open={!!createdContract} onOpenChange={(o) => !o && setCreatedContract(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contrat créé</DialogTitle>
          </DialogHeader>
          {createdContract && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Le contrat pour <b>{createdContract.prestataire_nom}</b> a été généré. Envoyez le lien
                ci-dessous au prestataire pour qu'il signe électroniquement.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <Label className="text-xs text-slate-500">Lien de signature unique</Label>
                <div className="flex items-center gap-2 mt-1">
                  <LinkIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <code className="text-xs flex-1 break-all">
                    {window.location.origin}
                    {createdContract.sign_url}
                  </code>
                </div>
              </div>
              <Button
                className="w-full"
                onClick={() => copyLink(createdContract.sign_url)}
                data-testid="created-copy-link"
              >
                <Copy className="h-4 w-4 mr-2" /> Copier le lien
              </Button>
              <p className="text-xs text-slate-500">
                Ce lien est secret — ne le partagez qu'avec le prestataire concerné.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatedContract(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
